import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export const EASE = [0.22, 1, 0.36, 1];

export const FadeUp = ({ children, delay = 0, className = "", y = 28, once = true }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-70px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    className={`reveal-block ${className}`}
  >
    {children}
  </motion.div>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Counter = ({ to, suffix = "", prefix = "", duration = 1600 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  const isFloat = !Number.isInteger(to);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = to * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, isFloat]);
  return (
    <span ref={ref}>
      {prefix}
      {val}
      {suffix}
    </span>
  );
};

export const Overline = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-semibold text-terracotta ${className}`}
  >
    <span className="w-8 h-px bg-saffron inline-block" />
    {children}
  </span>
);


export const TiltCard = ({ children, className = "", max = 9 }) => {
  const ref = useRef(null);
  const [style, setStyle] = useState({ transform: "perspective(900px) rotateX(0deg) rotateY(0deg)" });
  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `perspective(900px) rotateX(${(-py * max).toFixed(2)}deg) rotateY(${(px * max).toFixed(2)}deg)`,
      transition: "transform 0.08s linear",
    });
  };
  const onLeave = () =>
    setStyle({
      transform: "perspective(900px) rotateX(0deg) rotateY(0deg)",
      transition: "transform 0.5s cubic-bezier(0.22,1,0.36,1)",
    });
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className={`reveal-block ${className}`} style={{ ...style, willChange: "transform" }}>
      {children}
    </div>
  );
};
